import { Users } from "lucide-react";
import { useEffect, useRef } from "react";
import type { GroupChatMessage, SpecialistAgent } from "@/types";
import { AgentAvatar } from "./AgentAvatar";
import { GroupChatMessageBubble } from "./GroupChatMessageBubble";

const EXAMPLE_PROMPTS = [
  "Should we build this feature in-house or buy a vendor solution? Walk me through the tradeoffs.",
  "Review our Q3 strategy deck — what are we missing, and where are the biggest risks?",
  "We need to decide on a pricing model for the new product. What are the options and who does each one favor?",
];

interface GroupChatMessageListProps {
  messages: GroupChatMessage[];
  agents: SpecialistAgent[];
  isLoading?: boolean;
  thinkingAgent?: { name: string; icon?: string; color?: string } | null;
  onQuickAction?: (action: string) => void;
  onExamplePrompt?: (prompt: string) => void;
}

export function GroupChatMessageList({
  messages,
  agents,
  isLoading = false,
  thinkingAgent = null,
  onQuickAction,
  onExamplePrompt,
}: Readonly<GroupChatMessageListProps>) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change or thinking agent changes.
  // Use the container's scrollTop instead of scrollIntoView to avoid
  // scrolling the entire page (scrollIntoView bubbles to all ancestors).
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
    }
  }, [messages, isLoading, thinkingAgent]);

  if (messages.length === 0 && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-mutual-orange/20 to-mutual-purple/20 flex items-center justify-center mb-4">
          <Users className="w-8 h-8 text-brand-primary" />
        </div>
        <h2 className="text-2xl font-bold text-content-primary mb-2">Start a group discussion</h2>
        <p className="text-content-tertiary max-w-md">
          Pose a question and multiple specialist personas will debate, challenge, and build on each other's ideas to
          give you a well-rounded answer.
        </p>
        <div className="mt-5 flex flex-col gap-2 text-sm text-content-quaternary max-w-md w-full">
          <p className="text-xs font-semibold uppercase tracking-wider text-content-quaternary mb-1">Try asking</p>
          {EXAMPLE_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              onClick={() => onExamplePrompt?.(prompt)}
              className="bg-surface-primary/50 border border-border-primary rounded-lg px-4 py-2.5 text-content-tertiary text-left hover:border-border-secondary hover:text-content-secondary transition-colors cursor-pointer"
            >
              "{prompt}"
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex-1 overflow-y-auto p-6 space-y-6">
      {messages.map((message) => (
        <GroupChatMessageBubble key={message.uuid} message={message} agents={agents} onQuickAction={onQuickAction} />
      ))}

      {/* Loading indicator — shows which agent is thinking */}
      {isLoading && (
        <div className="flex gap-4 justify-center">
          <div className="max-w-2xl flex items-center gap-3 bg-surface-secondary/50 border border-border-secondary/50 rounded-lg px-4 py-3">
            {thinkingAgent ? (
              <>
                {/* Try to find the full agent object for the avatar */}
                {(() => {
                  const agent = agents.find((a) => a.name === thinkingAgent.name);
                  return agent ? (
                    <AgentAvatar agent={agent} size="sm" />
                  ) : (
                    <div
                      className="w-6 h-6 rounded-lg flex items-center justify-center text-xs shrink-0"
                      style={{ backgroundColor: thinkingAgent.color || "#6B7280" }}
                    >
                      {thinkingAgent.icon || thinkingAgent.name[0]}
                    </div>
                  );
                })()}
                <div className="flex items-center gap-2 text-sm text-content-secondary">
                  <span className="font-medium">{thinkingAgent.name}</span>
                  <span>is thinking</span>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-brand-primary rounded-full animate-bounce" />
                    <div
                      className="w-1.5 h-1.5 bg-brand-primary rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    />
                    <div
                      className="w-1.5 h-1.5 bg-brand-primary rounded-full animate-bounce"
                      style={{ animationDelay: "0.4s" }}
                    />
                  </div>
                </div>
              </>
            ) : (
              <>
                <Users className="w-4 h-4 text-brand-primary shrink-0" />
                <div className="flex items-center gap-2 text-sm text-content-secondary">
                  <span>Personas discussing</span>
                  <div className="flex items-center gap-1">
                    <div className="w-1.5 h-1.5 bg-brand-primary rounded-full animate-bounce" />
                    <div
                      className="w-1.5 h-1.5 bg-brand-primary rounded-full animate-bounce"
                      style={{ animationDelay: "0.2s" }}
                    />
                    <div
                      className="w-1.5 h-1.5 bg-brand-primary rounded-full animate-bounce"
                      style={{ animationDelay: "0.4s" }}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
